import "./style.scss";

import Close from "@mui/icons-material/Close";
import ArrowRight from "assets/img/svg/arrowRight";
import Checkmark from "assets/img/svg/checkmark";
import Supply from "assets/img/svg/supply";
import type { TFormCreateBeneficiar } from "components/BeneficiaryFormContainer/types";
import FormSidebar from "components/FormSidebar";
import type { INewStatementForm } from "components/PartnerStatementsSettings/types";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import type { TFormCreateTitle } from "components/TitleFormContainer/types";
import type { TPublicationData } from "containers/PublicationFormContainer";
import type { IFormStep, ITermFormValues, ITermMessagesOptions } from "containers/TermFormContainer";
import type { ITransfereeData } from "containers/TransfereesFormContainer";
import type { ITransferTermData } from "containers/TransferTermFormContainer";
import { preventSubmit } from "helpers/utils";
import { useKeyPress } from "hooks/useKeyPress";
import type { CSSProperties, Dispatch, SetStateAction } from "react";
import { forwardRef, useImperativeHandle, useState } from "react";
import type { UseFormHandleSubmit, UseFormTrigger } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ITerm } from "store/managementStore";
import type { TFetchStatus } from "store/types";

import ExitModal from "./ExitModal";

// TODO: See if I can easily add all formdata types instead of any:
type TUseFormData = {
    trigger: UseFormTrigger<any>;
    handleSubmit: UseFormHandleSubmit<any, undefined>;
    isDirty: boolean;
    dirtyFields?: Partial<Readonly<any>>;
};

interface FormContainer {
    /**
     * Data and functions from the useForm hook needed in this component
     */
    useFormData: TUseFormData;
    /**
     * Triggers the onSubmit function in parent component
     */
    onSubmit:
        | ((form: TFormCreateTitle) => void)
        | ((data: ITermFormValues) => void)
        | ((data: TFormCreateBeneficiar) => void)
        | ((data: INewStatementForm) => void)
        | ((data: ITransfereeData) => void)
        | ((data: ITransferTermData) => void)
        | ((data: TPublicationData) => void)
        | (() => void);

    /**
     * Data needed for the FormSidebar component
     */
    dataForSteps: string[];
    /**
     * The components for each step
     */
    steps: IFormStep[];
    /**
     * Whether accessing form for a new creation or to edit
     * "edit-page", this mode allows to associate a specific style page while editing Title or Beneficiar
     */
    mode: "new" | "edit" | "edit-page";
    /**
     * Close edit mode for new title and beneficiar
     * */
    closeEditMode?: () => void;
    /**
     * Title and subtitle for the archive/delete section (last one)
     */
    deleteStep?: { title: string; subtitle: string };
    /**
     * Triggers delete function in parent
     */
    onDelete: () => void;
    /**
     * Specific to Term form: boolean used to trigger save button.
     */
    haveInDepthValuesChanged?: boolean;
    /**
     * What will complete the Return button (can be name of Title, Beneficiary, etc.)
     */
    returnTo?: string;
    /**
     * This status is specific to CreateTitle, to catch the submit form status for visual loading effect submit button
     */
    submitFormStatus?: TFetchStatus;
    /**
     * Name of the submit form button for mode = "new"
     */
    txtBtnSubmitNew: string;
    /**
     * Specific to edit beneficiary / title, fade edit style condition
     */
    editModeActive?: boolean;
    /**
     * Specify for which type of form is used
     */
    type?: "statement" | "title" | "beneficiary" | "article";
    /**
     * Needed on term form, to see if beneficiary has other terms.
     */
    setOtherTermsMessage?: Dispatch<SetStateAction<ITermMessagesOptions>>;
    /**
     * Custom scroll to errors before submit form (only works on edit)
     */
    handleScrollToErrors?: () => void;
    /**
     * Check if term needs to display a warning on certain changes (only needed when mode = "edit")
     */
    displaysWarning?: boolean;
    /**
     * To specify a route on close which isn't default (-1)
     */
    onCloseRoute?: string | { route: string; options: any };
    /**
     * Other behaviour when clicking on Cross icon.
     */
    handleCloseButton?: any;
    /**
     * Text and link for doc button.
     */
    infoLink?: { type: string; link: string; context?: "female" | "male" };
    /**
     * Custom render a message block in the form (indication message error or warning)
     */
    messageBlock?: JSX.Element | null;
}

/**
 * Reusable component for each form. Takes data from the parent container (one for each type of form) and renders each step. Handles passing from one step to the next (and vice versa).
 */

const NewForm = forwardRef<any, FormContainer>(function NewForm(props: FormContainer, ref) {
    const {
        useFormData,
        onSubmit,
        dataForSteps,
        steps,
        mode,
        closeEditMode,
        deleteStep,
        onDelete,
        haveInDepthValuesChanged,
        returnTo,
        submitFormStatus,
        txtBtnSubmitNew,
        editModeActive,
        type,
        setOtherTermsMessage,
        handleScrollToErrors,
        displaysWarning = false,
        onCloseRoute,
        handleCloseButton,
        infoLink,
        messageBlock,
    } = props;

    const [t] = useTranslation();
    const navigate = useNavigate();
    const { trigger, handleSubmit, isDirty, dirtyFields } = useFormData;

    const [blockModal, setBlockModal] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    /**
     * Changes step of the form (previous or next).
     * @param nextStep The destination step.
     */
    const blockForwardNavigation = () => {
        const blockForward = steps[currentStep]?.onBlockForward;
        if (!blockForward) return false;
        blockForward();
        return true;
    };

    const handleStepChange = async (nextStep: number) => {
        if (nextStep > currentStep) {
            if (blockForwardNavigation()) return;
            const pass = await trigger();
            if (!pass) return;
        }
        setCurrentStep(nextStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /**
     * Changes step to name clicked on sidebar (previous or next).
     * @param index The destination step.
     */
    const handleMoveToCurrentStep = async (index: number) => {
        if (currentStep < index) {
            if (blockForwardNavigation()) return;
            const pass = await trigger();
            if (!pass) return;
        }
        setCurrentStep(index);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Allows parent to access function through ref.
    useImperativeHandle(
        ref,
        () => {
            return {
                /**
                 * Change step without checking for errors.
                 */
                forceStepChange(index: number) {
                    setCurrentStep(index);
                },
            };
        },
        [],
    );

    const handleExitConfirm = () => {
        if (mode === "edit-page" || type === "article") {
            setBlockModal(false);
            closeEditMode && closeEditMode();
        }
        if (mode === "new") {
            setOtherTermsMessage &&
                setOtherTermsMessage((prev) => {
                    const messages = { ...prev };
                    Object.keys(messages).forEach((key) => {
                        messages[key] = Array.isArray(messages[key]) ? [] : false;
                    });
                    return messages;
                });
            if (onCloseRoute) {
                typeof onCloseRoute === "string"
                    ? navigate(onCloseRoute)
                    : navigate(onCloseRoute.route, onCloseRoute.options);
            } else if (handleCloseButton) {
                handleCloseButton();
            } else {
                navigate(-1);
            }
        }
    };

    const newStyle = {
        paddingInline: "17.25rem",
        flex: "1",
        paddingBottom: "3rem",
        overflow: "auto",
    };
    const editStyle = { paddingInline: "8rem", width: "60rem" };
    const editTitleStyle = { with: "100%", width: "600px" };

    const handleCloseWithBlockModal = () => {
        if (isDirty || haveInDepthValuesChanged) {
            setBlockModal(true);
        } else handleExitConfirm();
    };

    useKeyPress({
        keys: ["Escape"],
        callback: handleCloseWithBlockModal,
    });

    const showWarningMessageOverlay = () => {
        if (type === "title") {
            return dirtyFields?.price || dirtyFields?.shallRecompute;
        } else if (type === "beneficiary") {
            return dirtyFields?.precount || dirtyFields?.socialProfileType || dirtyFields?.vat;
        }
    };

    const saveEditsButton =
        (isDirty || haveInDepthValuesChanged) && mode === "edit" ? (
            <div className="edit-btn-container">
                <StyledButton
                    className="edit-btn"
                    loading={submitFormStatus === "pending"}
                    disabled={submitFormStatus === "pending"}
                    type="submit"
                    buttonType="primary"
                    onClick={handleScrollToErrors}
                    form="form"
                >
                    {submitFormStatus === "pending" ? "" : t("ConnectTitle.SaveEdits")}
                </StyledButton>
            </div>
        ) : null;

    return (
        <div className={`${mode === "new" ? "modal-effect" : ""}`}>
            <div
                className={`new-form-container ${mode === "new" ? "new" : "edit"}`}
                style={mode === "edit-page" ? { display: "flex", justifyContent: "center" } : {}}
            >
                <ExitModal open={blockModal} onConfirm={handleExitConfirm} onCancel={() => setBlockModal(false)} />
                {mode === "new" && (
                    <FormSidebar
                        steps={steps}
                        currentStep={currentStep}
                        data={dataForSteps}
                        onExit={handleCloseWithBlockModal}
                        onStepChange={(step: number) => handleMoveToCurrentStep(step)}
                        exitBtnText={returnTo}
                        infoLink={infoLink}
                    />
                )}
                {mode === "new" && (
                    <div className="form new" style={newStyle}>
                        <StyledButton buttonType="secondary" className="exit-btn" onClick={handleCloseWithBlockModal}>
                            <span>{t("ConnectTitle.Leave")}</span>
                            <Close />
                        </StyledButton>
                        <form onKeyDown={preventSubmit} onSubmit={handleSubmit(onSubmit)}>
                            {steps.map((step, index) => (
                                <div key={`step-${index}`}>
                                    {currentStep === index && (
                                        <section
                                            className={`step-${index} new`}
                                            id={`step-${index}`}
                                            style={{ paddingTop: "5rem" }}
                                        >
                                            {step.format(step.stepTitle, step.stepSubtitle)}
                                            <div className={`navigation-btns ${index === 0 ? "noPrevious" : ""}`}>
                                                {index > 0 && (
                                                    <StyledButton
                                                        className="previous"
                                                        buttonType="outlined"
                                                        onClick={() => handleStepChange(currentStep - 1)}
                                                    >
                                                        {t("ConnectTitle.PreviousStep")}
                                                    </StyledButton>
                                                )}
                                                {index !== steps.length - 1 && (
                                                    <StyledButton
                                                        className="next"
                                                        buttonType="primary"
                                                        disabled={currentStep === index && step.conditionToDisable}
                                                        onClick={() => handleStepChange(currentStep + 1)}
                                                    >
                                                        {t("ConnectTitle.NextStep")} <ArrowRight />
                                                    </StyledButton>
                                                )}
                                                {index === steps.length - 1 && (
                                                    <StyledButton
                                                        type="submit"
                                                        className="submit-form-new-btn"
                                                        loading={submitFormStatus === "pending"}
                                                        disabled={
                                                            submitFormStatus === "pending" ||
                                                            (currentStep === index && step.conditionToDisable)
                                                        }
                                                        buttonType="primary"
                                                        onClick={handleScrollToErrors}
                                                    >
                                                        {submitFormStatus === "pending" ? "" : txtBtnSubmitNew}
                                                    </StyledButton>
                                                )}
                                            </div>
                                        </section>
                                    )}
                                </div>
                            ))}
                        </form>
                    </div>
                )}

                {(mode === "edit" || mode === "edit-page") && (
                    <>
                        {/** FADE IN OVERLAY FOR EDIT MODE BENEFICIARY / TITLE */}
                        {editModeActive && type !== "article" && (
                            <div className="fade-edit-mode-overlay" onClick={handleCloseWithBlockModal} />
                        )}
                        <div
                            className={
                                mode === "edit-page"
                                    ? "fade-edit-mode-container"
                                    : type === "article"
                                      ? "edit-mode-press"
                                      : ""
                            }
                            style={!editModeActive && mode === "edit-page" ? { opacity: 0, pointerEvents: "none" } : {}}
                            tabIndex={-1}
                        >
                            <div className="form edit" style={mode === "edit-page" ? editTitleStyle : editStyle}>
                                <form
                                    onKeyDown={preventSubmit}
                                    onSubmit={handleSubmit(onSubmit)}
                                    style={mode === "edit-page" ? { paddingBottom: "100px" } : {}}
                                    id="form"
                                >
                                    {steps.map((step, index) => (
                                        <div key={`step-${index}`}>
                                            <section
                                                className={`step-${index} edit`}
                                                id={`step-${index}`}
                                                style={{ paddingTop: "3rem" }}
                                            >
                                                {step.format(step.stepTitle, step.stepSubtitle)}
                                            </section>
                                            {mode === "edit-page" && <br />}
                                        </div>
                                    ))}
                                    <section className="step-delete" style={{ paddingTop: "3rem" }}>
                                        {deleteStep && (
                                            <>
                                                <h3 className="form-section-title first">
                                                    {" "}
                                                    {steps.length + 1}. {deleteStep?.title}
                                                </h3>
                                                <span className="form-section-description">{deleteStep?.subtitle}</span>
                                                <button
                                                    className="archive-btn"
                                                    type="button"
                                                    onClick={() => onDelete()}
                                                >
                                                    <Supply />
                                                    {deleteStep?.title}
                                                </button>
                                            </>
                                        )}
                                    </section>

                                    {/* Renders warning message about payment passing to Unpaid - only for term form */}
                                    {mode === "edit" &&
                                        displaysWarning &&
                                        (dirtyFields?.advancePayment ||
                                            haveInDepthValuesChanged ||
                                            dirtyFields?.compensate) && (
                                            <MessageBlock
                                                type="warning"
                                                text={t("ConnectTitle.TermChangeWarning")}
                                                style={
                                                    { position: "sticky", zIndex: 5, bottom: "65px" } as CSSProperties
                                                }
                                            />
                                        )}
                                    {messageBlock}

                                    {/* Renders warning message on edit-overlay forms (title & beneficiary) */}
                                    {isDirty && mode === "edit-page" && (
                                        <>
                                            <div className="edit-page-footer">
                                                {showWarningMessageOverlay() && (
                                                    <MessageBlock
                                                        type="warning"
                                                        text={
                                                            type === "title"
                                                                ? t("NewTitle.RecomputationWarning")
                                                                : t("NewBeneficiary.RecomputationWarning")
                                                        }
                                                        style={{ marginBottom: "10px" } as CSSProperties}
                                                    />
                                                )}
                                                <StyledButton
                                                    type="submit"
                                                    loading={submitFormStatus === "pending"}
                                                    disabled={submitFormStatus === "pending"}
                                                    className="edit-page-footer-btn"
                                                    buttonType="primary"
                                                    onClick={handleScrollToErrors}
                                                >
                                                    {submitFormStatus === "pending" ? (
                                                        ""
                                                    ) : (
                                                        <>
                                                            <Checkmark /> {t("Settings.SaveChanges")}
                                                        </>
                                                    )}
                                                </StyledButton>
                                            </div>
                                        </>
                                    )}
                                </form>
                            </div>
                            {(mode === "edit-page" || type === "statement") && (
                                <div className="edit-page-close">
                                    <StyledButton onClick={handleCloseWithBlockModal} buttonType="secondary">
                                        {t("NewElement.Quit")}
                                    </StyledButton>
                                </div>
                            )}
                            {/* Press article edit scrolls inside this column. Keep the save button here so it stays on screen. */}
                            {type === "article" && saveEditsButton}
                        </div>
                    </>
                )}
                {type !== "article" && saveEditsButton}
            </div>
        </div>
    );
});

export default NewForm;
